/**
 * Wishlist feature
 * - Persists saved products in localStorage (no login required)
 * - Toggles heart buttons on product cards / PDP
 * - Renders a slide-out drawer with Add to Cart / Buy Now / Remove
 * - Updates header (desktop) and bottom-nav (mobile) counts
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mooncee:wishlist';

  var Wishlist = {
    items: [],

    init: function () {
      this.items = this.load();
      this.drawer = document.getElementById('WishlistDrawer');
      this.cache();
      this.bindEvents();
      this.syncHeartStates();
      this.renderDrawer();
      this.updateCounts();
      this.watchForNewCards();
    },

    cache: function () {
      this.itemsContainer = this.drawer ? this.drawer.querySelector('[data-wishlist-items]') : null;
      this.emptyState = this.drawer ? this.drawer.querySelector('[data-wishlist-empty]') : null;
      this.template = document.getElementById('WishlistItemTemplate');
      this.moneyFormat = (this.drawer && this.drawer.getAttribute('data-money-format')) || '{{ amount }}';
      this.cartAddUrl = (this.drawer && this.drawer.getAttribute('data-cart-add-url')) || '/cart/add';
    },

    /* ---------------- storage ---------------- */

    load: function () {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    },

    persist: function () {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        /* storage unavailable (private mode / quota) - fail silently */
      }
    },

    has: function (productId) {
      return this.items.some(function (item) {
        return String(item.id) === String(productId);
      });
    },

    add: function (item) {
      if (this.has(item.id)) return;
      this.items.unshift(item);
      this.persist();
    },

    remove: function (productId) {
      this.items = this.items.filter(function (item) {
        return String(item.id) !== String(productId);
      });
      this.persist();
    },

    /* ---------------- events ---------------- */

    bindEvents: function () {
      var self = this;

      document.addEventListener('click', function (evt) {
        var toggleBtn = evt.target.closest('[data-wishlist-toggle]');
        if (toggleBtn) {
          evt.preventDefault();
          self.handleToggle(toggleBtn);
          return;
        }

        var openBtn = evt.target.closest('.js-wishlist-open');
        if (openBtn) {
          evt.preventDefault();
          self.open();
          return;
        }

        var closeBtn = evt.target.closest('[data-wishlist-close]');
        if (closeBtn) {
          evt.preventDefault();
          self.close();
          return;
        }

        var removeBtn = evt.target.closest('[data-wishlist-remove]');
        if (removeBtn) {
          evt.preventDefault();
          self.handleRemove(removeBtn);
          return;
        }

        var addToCartBtn = evt.target.closest('[data-wishlist-add-to-cart]');
        if (addToCartBtn) {
          evt.preventDefault();
          self.handleAddToCart(addToCartBtn, false);
          return;
        }

        var buyNowBtn = evt.target.closest('[data-wishlist-buy-now]');
        if (buyNowBtn) {
          evt.preventDefault();
          self.handleAddToCart(buyNowBtn, true);
          return;
        }
      });

      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' && self.drawer && self.drawer.classList.contains('is-open')) {
          self.close();
        }
      });
    },

    /* Watch for product cards injected later (infinite scroll, AJAX filters, quick shop, etc.) */
    watchForNewCards: function () {
      var self = this;
      if (!('MutationObserver' in window)) return;
      var observer = new MutationObserver(function (mutations) {
        var needsSync = false;
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType === 1 && (node.matches && node.matches('[data-wishlist-toggle]') || (node.querySelector && node.querySelector('[data-wishlist-toggle]')))) {
              needsSync = true;
            }
          });
        });
        if (needsSync) self.syncHeartStates();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    },

    /* ---------------- heart buttons ---------------- */

    handleToggle: function (btn) {
      var productId = btn.getAttribute('data-product-id');
      var isSaved = this.has(productId);

      if (isSaved) {
        this.remove(productId);
      } else {
        this.add({
          id: productId,
          variantId: btn.getAttribute('data-variant-id'),
          handle: btn.getAttribute('data-product-handle'),
          title: btn.getAttribute('data-product-title'),
          price: parseInt(btn.getAttribute('data-product-price'), 10) || 0,
          image: btn.getAttribute('data-product-image'),
          url: btn.getAttribute('data-product-url'),
          available: btn.getAttribute('data-product-available') === 'true'
        });
      }

      this.syncHeartStates();
      this.renderDrawer();
      this.updateCounts();

      btn.classList.add('is-animating');
      window.setTimeout(function () {
        btn.classList.remove('is-animating');
      }, 450);

      this.toast(isSaved ? 'Removed from wishlist' : 'Added to wishlist');
    },

    syncHeartStates: function () {
      var self = this;
      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
        var productId = btn.getAttribute('data-product-id');
        var saved = self.has(productId);
        btn.classList.toggle('is-wishlisted', saved);
        btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      });
    },

    /* ---------------- drawer ---------------- */

    open: function () {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    renderDrawer: function () {
      if (!this.itemsContainer || !this.template) return;

      this.itemsContainer.innerHTML = '';

      if (this.items.length === 0) {
        this.itemsContainer.hidden = true;
        if (this.emptyState) this.emptyState.hidden = false;
        return;
      }

      this.itemsContainer.hidden = false;
      if (this.emptyState) this.emptyState.hidden = true;

      var self = this;
      this.items.forEach(function (item) {
        var node = self.template.content.firstElementChild.cloneNode(true);
        node.setAttribute('data-product-id', item.id);
        node.setAttribute('data-variant-id', item.variantId || '');
        node.classList.add('is-entering');

        var links = node.querySelectorAll('[data-wishlist-item-link]');
        links.forEach(function (a) {
          a.setAttribute('href', item.url || '#');
        });

        var img = node.querySelector('[data-wishlist-item-image]');
        if (img) {
          img.setAttribute('src', item.image || '');
          img.setAttribute('alt', item.title || '');
        }

        var title = node.querySelector('[data-wishlist-item-title]');
        if (title) title.textContent = item.title || '';

        var price = node.querySelector('[data-wishlist-item-price]');
        if (price) price.textContent = self.formatMoney(item.price);

        var addBtn = node.querySelector('[data-wishlist-add-to-cart]');
        var buyBtn = node.querySelector('[data-wishlist-buy-now]');
        if (item.available === false) {
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.textContent = 'Sold out';
          }
          if (buyBtn) buyBtn.disabled = true;
        }

        self.itemsContainer.appendChild(node);
      });
    },

    handleRemove: function (btn) {
      var itemEl = btn.closest('[data-wishlist-item]');
      var productId = itemEl ? itemEl.getAttribute('data-product-id') : null;
      if (!productId) return;

      if (itemEl) {
        itemEl.classList.add('is-removing');
      }

      var self = this;
      window.setTimeout(function () {
        self.remove(productId);
        self.syncHeartStates();
        self.renderDrawer();
        self.updateCounts();
      }, 280);
    },

    /* ---------------- cart actions ---------------- */

    handleAddToCart: function (btn, redirectToCheckout) {
      var itemEl = btn.closest('[data-wishlist-item]');
      var variantId = itemEl ? itemEl.getAttribute('data-variant-id') : null;
      if (!variantId) return;

      var self = this;
      btn.classList.add('is-loading');

      fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function (res) { return res.json(); })
        .then(function () {
          btn.classList.remove('is-loading');

          if (redirectToCheckout) {
            window.location.href = '/checkout';
            return;
          }

          btn.classList.add('is-added');
          var originalText = btn.textContent;
          btn.textContent = 'Added';
          window.setTimeout(function () {
            btn.classList.remove('is-added');
            btn.textContent = originalText;
          }, 1500);

          self.toast('Added to cart');

          // Refresh the theme's cart drawer / bubble if present, without navigating away
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
              document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              var bubble = document.querySelector('.cart-link__bubble');
              if (bubble) bubble.classList.add('cart-link__bubble--visible');
            })
            .catch(function () {});
        })
        .catch(function (err) {
          btn.classList.remove('is-loading');
          console.error('Wishlist add to cart error:', err);
          self.toast('Could not add to cart');
        });
    },

    /* ---------------- counts / helpers ---------------- */

    updateCounts: function () {
      var count = this.items.length;

      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count;
      });

      document.querySelectorAll('.wishlist-link__bubble').forEach(function (el) {
        el.classList.toggle('is-visible', count > 0);
      });

      document.querySelectorAll('.wishlist-nav-link__bubble').forEach(function (el) {
        el.textContent = count;
        el.classList.toggle('is-visible', count > 0);
      });

      document.querySelectorAll('.wishlist-nav-link').forEach(function (el) {
        el.classList.toggle('is-active', count > 0);
      });
    },

    formatMoney: function (cents) {
      if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
      var value = (cents / 100).toFixed(2);
      var format = this.moneyFormat || '${{amount}}';
      var withoutDecimals = Math.floor(cents / 100).toString();

      return format
        .replace(/\{\{\s*amount\s*\}\}/, value)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withoutDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
    },

    toast: function (message) {
      var el = document.querySelector('.wishlist-toast');
      if (!el) {
        el = document.createElement('div');
        el.className = 'wishlist-toast';
        document.body.appendChild(el);
      }
      el.textContent = message;
      el.classList.add('is-visible');

      window.clearTimeout(this._toastTimer);
      this._toastTimer = window.setTimeout(function () {
        el.classList.remove('is-visible');
      }, 1600);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Wishlist.init(); });
  } else {
    Wishlist.init();
  }

  window.Wishlist = Wishlist;
})();
